import { type ReactNode, useEffect } from 'react'
import { IntlProvider } from 'react-intl'
import type { Locale } from './locales'
import { en } from './messages/en'
import { ja } from './messages/ja'
import { ko } from './messages/ko'
import { supplementalMessages } from './messages/supplemental'
import { zhCN } from './messages/zh-CN'
import { zhTW } from './messages/zh-TW'
import type { Messages } from './types'

export function flattenMessages(
  obj: object,
  prefix = '',
  out: Record<string, string> = {},
): Record<string, string> {
  for (const [key, value] of Object.entries(obj)) {
    const id = prefix ? `${prefix}.${key}` : key
    if (typeof value === 'string') out[id] = value
    else if (value && typeof value === 'object') flattenMessages(value, id, out)
  }
  return out
}

const dictionaries = { 'zh-CN': zhCN, 'zh-TW': zhTW, en, ja, ko } satisfies Record<Locale, Messages>
export const localeMessages = Object.fromEntries(
  Object.entries(dictionaries).map(([locale, messages]) => [
    locale,
    flattenMessages({ ...messages, ...supplementalMessages[locale as Locale] }),
  ]),
) as Record<Locale, Record<string, string>>

export function LocaleProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  return (
    <IntlProvider locale={locale} defaultLocale={locale} messages={localeMessages[locale]}>
      {children}
    </IntlProvider>
  )
}
